import React, { useState } from 'react';
import { Mail, Send } from 'lucide-react';
import { useLeadSubmit } from '../../hooks/useLeadSubmit';
import { BLOG_SUBSCRIBE_RESET_MS } from '../../constants/site';

const INPUT_CLS = 'w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:bg-white/15';
const EMPTY = { name: '', email: '', consent: false };

const SubscribeSuccess = () => (
  <div data-testid="blog-subscribe-success" className="bg-gold-400 text-[#143160] rounded-xl p-6 text-center">
    <div className="text-3xl mb-2">✓</div>
    <h4 className="font-bold text-lg mb-1">You're subscribed!</h4>
    <p className="text-sm">Thanks for joining. Jess will be in touch with her next coastal insight.</p>
  </div>
);

const SubscribeForm = ({ form, setForm, onSubmit, submitting, openPrivacyPolicy }) => (
  <form onSubmit={onSubmit} className="space-y-3" data-testid="blog-subscribe-form">
    <input data-testid="blog-subscribe-name-input" type="text" aria-label="Your name (optional)" autoComplete="name" placeholder="Your Name (optional)" className={INPUT_CLS} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
    <input data-testid="blog-subscribe-email-input" type="email" required aria-label="Email address" autoComplete="email" placeholder="Email Address*" className={INPUT_CLS} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
    <label className="flex items-start space-x-2 text-xs text-white/75 leading-relaxed cursor-pointer">
      <input data-testid="blog-subscribe-consent-checkbox" type="checkbox" className="mt-0.5 w-4 h-4 accent-gold-400" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} />
      <span>I agree to receive blog updates and occasional real estate insights from Jess Sells East Coast. Unsubscribe any time. See our <button type="button" onClick={openPrivacyPolicy} className="underline text-gold-400 hover:text-gold-300">Privacy Policy</button>.</span>
    </label>
    <button data-testid="blog-subscribe-submit-button" type="submit" disabled={submitting} className="w-full bg-gold-400 hover:bg-gold-300 text-[#143160] font-bold py-3 rounded-lg flex items-center justify-center group transition-all shadow-lg hover:shadow-gold-400/50 disabled:opacity-70 disabled:cursor-wait">
      {submitting ? 'Subscribing…' : 'Subscribe'}<Send className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
    </button>
  </form>
);

export const NewsletterSignup = ({ openPrivacyPolicy }) => {
  const [form, setForm] = useState(EMPTY);
  const { submitted, submitting, submit } = useLeadSubmit({
    endpoint: '/leads/blog-subscribe',
    errorLabel: 'blog subscriber',
    resetMs: BLOG_SUBSCRIBE_RESET_MS,
    onSuccess: () => setForm(EMPTY),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    submit({ name: form.name || null, email: form.email, consent: form.consent });
  };

  return (
    <div className="sticky top-36 bg-gradient-to-br from-[#143160] to-[#1E4A86] rounded-2xl p-8 text-white shadow-2xl border border-gold-400/30">
      <div className="flex items-center space-x-2 mb-4">
        <div className="w-10 h-10 bg-gold-400 rounded-lg flex items-center justify-center"><Mail className="w-5 h-5 text-[#143160]" /></div>
        <span className="text-xs font-bold tracking-widest text-gold-400 uppercase">Free Newsletter</span>
      </div>
      <h3 className="text-2xl font-serif mb-3 leading-tight">Never Miss a Coastal Market Update</h3>
      <p className="text-sm text-white/80 mb-6 leading-relaxed">Get Jess's latest blog posts, market reports, and insider tips for Hampton Roads &amp; the Outer Banks delivered straight to your inbox.</p>
      {submitted ? <SubscribeSuccess /> : <SubscribeForm form={form} setForm={setForm} onSubmit={handleSubmit} submitting={submitting} openPrivacyPolicy={openPrivacyPolicy} />}
    </div>
  );
};
